import React from 'react';
import { createLogo, createSealOrSign } from '../../utils/imageUtils';

function formatINR(num) {
  if (isNaN(num)) return '';
  return Number(num).toLocaleString('en-IN');
}

function numberToWordsIndian(num) {
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function inWords(n) {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
    if (n < 100000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
    return inWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
  }
  return inWords(Math.floor(num));
}

function numToWords(num) {
  return numberToWordsIndian(num) + ' Rupees Only';
}

function PurchaseBillNonGstTemplate({ billData = {}, companyDetails = {}, partyDetails = {}, bankDetails = {} }) {
  // Filter out empty placeholder rows (no item/zero values)
  const visibleItems = (billData.items || billData.rows || []).filter(it => {
    const hasDesc = !!(it.description && String(it.description).trim());
    const hasItem = !!(it.item && String(it.item).trim());
    const hasNumbers = (parseFloat(it.qty) || 0) > 0 || (parseFloat(it.rate) || 0) > 0 || (parseFloat(it.amount) || 0) > 0 || (parseFloat(it.total) || 0) > 0;
    return hasDesc || hasItem || hasNumbers;
  });
  
  const subtotal = visibleItems.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const discount = billData.discountType === 'percent'
    ? subtotal * (parseFloat(billData.discountValue) || 0) / 100
    : parseFloat(billData.discountValue) || 0;
  const discountedSubtotal = Math.max(0, subtotal - discount);
  
  // For non-GST bills, total equals subtotal
  const grandTotal = discountedSubtotal;
  
  const terms = companyDetails.purchaseTerms || '';

  return (
    <div className="p-4 md:p-8 bg-white rounded shadow max-w-3xl w-full mx-auto text-sm print:p-0 print:shadow-none print:bg-white">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center">
            {createLogo(companyDetails.logoUrl, companyDetails.firmName || 'Company', 'h-16 w-auto')}
          </div>
          <div>
            <div className="text-xl font-bold uppercase">{companyDetails.firmName}</div>
            <div>{companyDetails.address}{companyDetails.city ? ', ' + companyDetails.city : ''}{companyDetails.pincode ? ' - ' + companyDetails.pincode : ''}</div>
            <div>{companyDetails.state}</div>
            <div>GSTIN: {companyDetails.gstin || 'N/A'}</div>
            <div>Contact: {companyDetails.contactNumber || ''} | {companyDetails.email || ''}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold tracking-wider">PURCHASE BILL</div>
          <div className="mt-2"><span className="font-semibold">Bill No:</span> {billData.purchaseBillNumber || billData.number || ''}</div>
          <div><span className="font-semibold">Date:</span> {billData.date || billData.invoiceDate || ''}</div>
        </div>
      </div>
      
      {/* Parties */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-2">
        <div>
          <div className="font-semibold">Billed To (Buyer):</div>
          <div>{companyDetails.firmName}</div>
          <div>{companyDetails.address}</div>
          <div>{companyDetails.city} {companyDetails.pincode}</div>
          <div>GSTIN: {companyDetails.gstin || 'N/A'}</div>
          <div>Contact: {companyDetails.contactNumber || ''}</div>
        </div>
        <div className="text-right">
          <div className="font-semibold">Bill From (Seller):</div>
          <div>{partyDetails.firmName || partyDetails.name || ''}</div>
          <div>{partyDetails.address || ''}</div>
          <div>{partyDetails.city || ''} {partyDetails.pincode || ''}</div>
          <div>GSTIN: {partyDetails.gstin || 'N/A'}</div>
          <div>Contact: {partyDetails.contactNumber || ''}</div>
        </div>
      </div>
      
      {/* Item Table */}
      <div className="overflow-x-auto mt-4 mb-4">
        <table className="w-full min-w-[640px] border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1">Sr.</th>
              <th className="border px-2 py-1">Description</th>
              <th className="border px-2 py-1">HSN</th>
              <th className="border px-2 py-1">Qty</th>
              <th className="border px-2 py-1">Rate</th>
              <th className="border px-2 py-1">Amount</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((row, idx) => (
              <tr key={idx}>
                <td className="border px-2 py-1 text-center">{idx + 1}</td>
                <td className="border px-2 py-1">{row.description || row.itemName || ''}
                  <div style={{ fontSize: '0.85em', fontStyle: 'italic', color: '#555' }}>
                    {row.qtyDisplay ? row.qtyDisplay : `${row.nos || row.qty || 1}x${row.length || 1}x${row.height || 1}`}
                  </div>
                </td>
                <td className="border px-2 py-1 text-center">{row.hsn || row.hsnCode || ''}</td>
                <td className="border px-2 py-1 text-center">
                  {row.qty || row.nos || 1} {row.unit || row.quantityMeasurement || 'Nos.'}
                </td>
                <td className="border px-2 py-1 text-right">₹{formatINR(row.rate || 0)}</td>
                <td className="border px-2 py-1 text-right">₹{formatINR(row.amount || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Totals */}
      <div className="flex justify-end mb-4">
        <div className="w-64">
          <div className="flex justify-between py-1">
            <span>Subtotal:</span>
            <span>₹{formatINR(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between py-1">
              <span>Discount:</span>
              <span>-₹{formatINR(discount)}</span>
            </div>
          )}
          <div className="flex justify-between py-2 font-bold border-t">
            <span>Total Amount:</span>
            <span>₹{formatINR(grandTotal)}</span>
          </div>
        </div>
      </div>
      
      {/* Amount in Words */}
      <div className="mb-4">
        <div className="font-semibold">Amount in Words:</div>
        <div className="italic">{numToWords(grandTotal)}</div>
      </div>
      
      {/* Notes */}
      {billData.notes && (
        <div className="mb-4">
          <div className="font-semibold">Notes:</div>
          <div>{billData.notes}</div>
        </div>
      )}
      
      {/* Terms */}
      {terms && (
        <div className="mb-4">
          <div className="font-semibold">Terms & Conditions:</div>
          <div>{terms}</div>
        </div>
      )}
      
      {/* Footer */}
      <div className="flex justify-between items-end mt-8">
        <div className="text-center">
          <div className="border-t border-gray-400 pt-2 w-32">
            <div className="text-sm">Buyer's Signature</div>
          </div>
        </div>
        <div className="text-center">
          <div className="border-t border-gray-400 pt-2 w-32">
            <div className="text-sm">Seller's Signature</div>
          </div>
        </div>
      </div>
      
      {/* Non-GST Notice */}
      <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded text-center">
        <div className="text-sm font-medium text-yellow-800">
          This is a Non-GST Bill. No GST calculation is applicable.
        </div>
      </div>
    </div>
  );
}

export default PurchaseBillNonGstTemplate;
